import PropTracker from "../prop-trackers/prop-tracker.js";
import ValueTracker from "../prop-trackers/value-tracker.js";
import isObject from "../struct-events/is-object.js";
import { Combiner as Merger, ValueCombiner as ValueMerger } from "../prop-tracker-utils/combiner.js";
import ShallowObjectMerger from "./shallow-object-merger.js";
import copyDictTrackerToProps from "../prop-tracker-utils/copy-dict-tracker-to-props.js";

function isEmpty(obj) {
    for (const key in obj) {
        return false;
    }

    return true;
}

/*
 * Very similar to DeepObjectMerger, only
 * handles "length" property differently
 */
class DeepArrayMerger extends ValueMerger {
    constructor(wrapCtx, evtCtx) {
        super();

        this._evtCtx = evtCtx;
        this._destination = wrapCtx.getWrapper([]);

        this._merger = new Merger((key) => {
            if (key === "length") {
                return new ArrayLengthMerger();
            } else {
                return new DeepValueMerger(wrapCtx, evtCtx, key);
            }
        });

        this._sourceTrackers = {};

        this._tracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._merger.listenNeedsUpdate(needsUpdate);
        this._tracker.listenNeedsUpdate(needsUpdate);
    }

    setValue(id, value) {
        let sourceTracker = this._sourceTrackers[id];

        if (!sourceTracker) {
            if (isEmpty(this._sourceTrackers)) {
                this._tracker.setValue(this._destination);
            }

            sourceTracker = new PropTracker();
            this._sourceTrackers[id] = sourceTracker;
            this._merger.setSource(id, sourceTracker);
        }

        sourceTracker.track(value, this._evtCtx.getEvents(value));
    }

    deleteValue(id) {
        const sourceTracker = this._sourceTrackers[id];

        if (sourceTracker) {
            sourceTracker.track();
            this._merger.deleteSource(id);
            delete this._sourceTrackers[id];

            if (isEmpty(this._sourceTrackers)) {
                this._tracker.deleteValue();
            }
        }
    }

    protected_startUpdate() {
        this._merger.update(({ props }) => {
            for (const { key, newExists, newValue } of props.iterateModified()) {
                if (newExists) {
                    this._destination[key] = newValue;
                } else {
                    if (key !== "length") {
                        delete this._destination[key];
                    }
                }
            }
        });

        return this._tracker.startUpdate();
    }

    protected_finishUpdate() {
        this._tracker.finishUpdate();
    }
}

class ArrayLengthMerger extends ValueMerger {
    constructor() {
        super();

        this._values = {};
        this._tracker = new ValueTracker();
    }

    setValue(id, newLength) {
        this._values[id] = newLength;
        this.protected_needsUpdate();
    }

    deleteValue(id) {
        delete this._values[id];
        this.protected_needsUpdate();
    }

    protected_startUpdate() {
        if (isEmpty(this._values)) {
            this._tracker.deleteValue();
        } else {
            let maxLength = 0;

            for (const id in this._values) {
                const length = this._values[id];
                maxLength = Math.max(maxLength, length);
            }

            this._tracker.setValue(maxLength);
        }

        return this._tracker.startUpdate();
    }

    protected_finishUpdate() {
        this._tracker.finishUpdate();
    }
}

class DeepObjectMerger extends ValueMerger {
    constructor(wrapCtx, evtCtx) {
        super();

        this._evtCtx = evtCtx;
        this._destination = wrapCtx.getWrapper({});

        this._merger = new Merger((key) => new DeepValueMerger(wrapCtx, evtCtx, key));
        this._sourceTrackers = {};
        this._tracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._merger.listenNeedsUpdate(needsUpdate);
        this._tracker.listenNeedsUpdate(needsUpdate);
    }

    setValue(id, value) {
        let sourceTracker = this._sourceTrackers[id];

        if (!sourceTracker) {
            if (isEmpty(this._sourceTrackers)) {
                this._tracker.setValue(this._destination);
            }

            sourceTracker = new PropTracker();
            this._sourceTrackers[id] = sourceTracker;
            this._merger.setSource(id, sourceTracker);
        }

        sourceTracker.track(value, this._evtCtx.getEvents(value));
    }

    deleteValue(id) {
        const sourceTracker = this._sourceTrackers[id];

        if (sourceTracker) {
            sourceTracker.track();
            this._merger.deleteSource(id);
            delete this._sourceTrackers[id];

            if (isEmpty(this._sourceTrackers)) {
                this._tracker.deleteValue();
            }
        }
    }

    protected_startUpdate() {
        copyDictTrackerToProps(this._destination, this._merger);

        return this._tracker.startUpdate();
    }

    protected_finishUpdate() {
        this._tracker.finishUpdate();
    }
}

function getFirstKey(obj) {
    for (const key in obj) {
        return key;
    }
}

function getValueType(value) {
    if (isObject(value)) {
        if (Array.isArray(value)) {
            return "array";
        } else {
            return "object";
        }
    } else {
        return "primitive";
    }
}

class DeepValueMerger extends ValueMerger {
    constructor(wrapCtx, evtCtx) {
        super();

        this._valueTypes = {};
        this._typeValues = {
            array: {},
            object: {},
            primitive: {},
        };

        this._typeMergers = {
            array: new DeepArrayMerger(wrapCtx, evtCtx),
            object: new DeepObjectMerger(wrapCtx, evtCtx),
            primitive: new ShallowObjectMerger(),
        };

        this._typeTracker = new ValueTracker();
        this._valueTracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._typeMergers.array.listenNeedsUpdate(needsUpdate);
        this._typeMergers.object.listenNeedsUpdate(needsUpdate);
        this._typeMergers.primitive.listenNeedsUpdate(needsUpdate);
    }

    setValue(id, value) {
        const newType = getValueType(value);
        const oldType = this._valueTypes[id];

        if (oldType !== newType) {
            if (oldType) {
                delete this._typeValues[oldType][id];
                this._typeMergers[oldType].deleteValue(id);
            }

            this._valueTypes[id] = newType;
        }

        this._typeValues[newType][id] = value;
        this._typeMergers[newType].setValue(id, value);
    }

    deleteValue(id) {
        const oldType = this._valueTypes[id];

        if (oldType) {
            delete this._typeValues[oldType][id];
            this._typeMergers[oldType].deleteValue(id);
        }

        delete this._valueTypes[id];
    }

    protected_startUpdate() {
        const type = this._valueTypes[getFirstKey(this._valueTypes)];
        this._typeTracker.setValue(type);

        for (const mergerType in this._typeMergers) {
            const merger = this._typeMergers[mergerType];

            merger.update(({ prop: { newExists, newValue } }) => {
                if (type === mergerType) {
                    if (newExists) {
                        this._valueTracker.setValue(newValue);
                    } else {
                        this._valueTracker.deleteValue();
                    }
                }
            });
        }
        
        return this._valueTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._valueTracker.finishUpdate();
    }
}

export default DeepValueMerger;
